import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useCinematicContext } from "@/components/landing/CinematicTransitionProvider";
import { VARIANTS } from "@/lib/landing/cinematicTransitions";
import crWordmarkWhite from "@/assets/cipher-row-wordmark-white.svg";

export function CinematicTransitionLayer() {
  const ctx = useCinematicContext();
  const navigate = useNavigate();
  const navigatedRef = useRef(false);
  const cancelledRef = useRef(false);

  const isActive = !!(ctx?.isTransitioning && ctx.currentOptions);
  const opts = ctx?.currentOptions;

  useEffect(() => {
    if (!isActive || !opts) return;

    navigatedRef.current = false;
    cancelledRef.current = false;

    const config = VARIANTS[opts.variant];
    const occlusionDelay = config.occlusionAt * config.maxDurationMs;

    // Set aria-busy on body for the duration
    document.body.setAttribute("aria-busy", "true");

    // Navigate at occlusion peak, optionally waiting for preload
    const navigateTimer = setTimeout(async () => {
      if (cancelledRef.current) return;

      if (opts.preload) {
        // Wait up to 250ms extra for the preload chunk to resolve
        try {
          await Promise.race([
            opts.preload(),
            new Promise<void>((resolve) => setTimeout(resolve, 250)),
          ]);
        } catch {
          // Proceed regardless — Suspense handles the fallback
        }
      }

      if (!cancelledRef.current) {
        navigatedRef.current = true;
        navigate(opts.to);
      }
    }, occlusionDelay);

    // Complete animation after full duration
    const completeTimer = setTimeout(() => {
      if (cancelledRef.current) return;
      document.body.removeAttribute("aria-busy");
      ctx?.onAnimationComplete();
    }, config.maxDurationMs);

    // Esc key cancels the in-flight transition
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !navigatedRef.current) {
        cancelledRef.current = true;
        clearTimeout(navigateTimer);
        clearTimeout(completeTimer);
        document.body.removeAttribute("aria-busy");
        navigate(opts.to);
        ctx?.onAnimationComplete();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(navigateTimer);
      clearTimeout(completeTimer);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.removeAttribute("aria-busy");
    };
  }, [isActive, opts, navigate, ctx]);

  // Build dynamic clip-path origin for inkdrop based on originPoint
  const getInkdropVariants = () => {
    if (!opts || opts.variant !== "inkdrop") return VARIANTS.inkdrop;
    const origin = opts.originPoint;
    if (!origin) return VARIANTS.inkdrop;

    const vw = window.innerWidth || 1280;
    const vh = window.innerHeight || 800;
    const xPct = Math.round((origin.x / vw) * 100);
    const yPct = Math.round((origin.y / vh) * 100);

    return {
      ...VARIANTS.inkdrop,
      exit: {
        hidden: { clipPath: `circle(0% at ${xPct}% ${yPct}%)`, opacity: 1 },
        visible: {
          clipPath: `circle(150% at ${xPct}% ${yPct}%)`,
          opacity: 1,
          transition: { duration: 0.54, ease: "easeIn" as const },
        },
      },
    };
  };

  const variantConfig = opts?.variant === "inkdrop" ? getInkdropVariants() : opts ? VARIANTS[opts.variant] : null;

  // Shutter needs two halves
  const isShutter = opts?.variant === "shutter";

  return (
    <AnimatePresence>
      {isActive && opts && variantConfig && (
        <motion.div
          key="cinematic-overlay"
          data-testid="cinematic-overlay"
          initial="hidden"
          animate="visible"
          exit="hidden"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          {isShutter ? (
            // Shutter: two halves slide out
            <>
              <motion.div
                initial={{ y: 0 }}
                animate={{ y: "-50vh" }}
                exit={{ y: 0 }}
                transition={{ duration: 0.375, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "50vh",
                  backgroundColor: "#0A0A0B",
                  pointerEvents: "none",
                }}
              />
              <motion.div
                initial={{ y: 0 }}
                animate={{ y: "50vh" }}
                exit={{ y: 0 }}
                transition={{ duration: 0.375, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "50vh",
                  backgroundColor: "#0A0A0B",
                  pointerEvents: "none",
                }}
              />
            </>
          ) : opts.variant === "logo-flip" ? (
            // Logo-flip: opaque black wash with the CR wordmark flipping in
            // 3D at center, an amber sweep crossing the screen, then the
            // overlay fades out to reveal the new page.
            <motion.div
              variants={variantConfig.exit}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#0A0A0B",
                pointerEvents: "none",
                overflow: "hidden",
                perspective: 1200,
              }}
            >
              <motion.div
                initial={{ x: "-110%" }}
                animate={{ x: "110%" }}
                transition={{ duration: 0.55, ease: [0.65, 0, 0.35, 1], delay: 0.04 }}
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  width: "55%",
                  left: 0,
                  background:
                    "linear-gradient(110deg, transparent 0%, rgba(255,176,32,0.0) 35%, rgba(255,176,32,0.22) 50%, rgba(255,176,32,0.0) 65%, transparent 100%)",
                  filter: "blur(2px)",
                }}
              />
              <motion.div
                initial={{ opacity: 0, rotateY: -90, scale: 0.9 }}
                animate={{ opacity: [0, 1, 1, 0], rotateY: [-90, 0, 0, 90], scale: [0.9, 1, 1, 0.95] }}
                transition={{ duration: 0.95, times: [0, 0.28, 0.7, 1], ease: [0.16, 1, 0.3, 1] }}
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transformStyle: "preserve-3d",
                }}
              >
                <img
                  src={crWordmarkWhite}
                  alt=""
                  aria-hidden="true"
                  style={{ height: 56, opacity: 0.95, filter: "drop-shadow(0 0 24px rgba(255,176,32,0.35))" }}
                />
              </motion.div>
            </motion.div>
          ) : (
            // All other variants: single overlay div
            <motion.div
              variants={variantConfig.exit}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#0A0A0B",
                pointerEvents: "none",
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
